import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { fetchSavedReportConfigs, deleteSavedReportConfig } from '../../graphql/reportQueries';
import '../../css/CustomReportsTab.css';

interface CustomReportsTabProps {
    companyId: string;
}

interface SavedConfig {
    id: string;
    reportName: string;
    metricKey: string;
    metricCategory?: string;
    lastRunAt?: string;
    createdAt: string;
}

/**
 * Custom Reports tab content.
 * Displays saved custom report configurations in a grid.
 * Click navigates to the report builder; kebab menu allows deletion.
 *
 * Validates: Requirements 10.2, 10.3, 10.4
 */
export const CustomReportsTab = ({ companyId }: CustomReportsTabProps) => {
    const navigate = useNavigate();

    const [configs, setConfigs] = useState<SavedConfig[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close kebab menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch saved configs
    useEffect(() => {
        if (!companyId) return;
        setIsLoading(true);
        setError(null);

        fetchSavedReportConfigs(companyId)
            .then((items) => {
                const mapped: SavedConfig[] = items.map((item: any) => ({
                    id: item.id,
                    reportName: item.reportName || 'Untitled Report',
                    metricKey: item.metricKey || '',
                    metricCategory: item.metricCategory || '',
                    lastRunAt: item.lastRunAt || '',
                    createdAt: item.createdAt || '',
                }));
                // Sort by createdAt descending
                mapped.sort((a, b) => (b.createdAt > a.createdAt ? 1 : b.createdAt < a.createdAt ? -1 : 0));
                setConfigs(mapped);
            })
            .catch((err) => {
                console.error('Failed to fetch saved report configs:', err);
                Sentry.captureException(err);
                setError('Failed to load saved reports. Please try again.');
            })
            .finally(() => setIsLoading(false));
    }, [companyId]);

    const handleCardClick = (configId: string) => {
        navigate(`/reports/custom/${configId}`);
    };

    const handleDelete = useCallback(
        async (configId: string) => {
            setOpenMenuId(null);
            // Optimistic removal
            const prev = configs;
            setConfigs((c) => c.filter((item) => item.id !== configId));
            try {
                await deleteSavedReportConfig(configId);
            } catch (err) {
                console.error('Failed to delete saved report config:', err);
                Sentry.captureException(err);
                setConfigs(prev); // Revert
            }
        },
        [configs],
    );

    const handleKebabClick = (e: React.MouseEvent, configId: string) => {
        e.stopPropagation();
        setOpenMenuId((prev) => (prev === configId ? null : configId));
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    const formatMetricLabel = (metricKey: string) => {
        // Convert kebab-case to title case
        return metricKey
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (isLoading) {
        return <div className="custom-reports-loading">Loading saved reports...</div>;
    }

    if (error) {
        return (
            <div className="custom-reports-empty">
                <div className="custom-reports-empty-title">{error}</div>
            </div>
        );
    }

    return (
        <div className="custom-reports-tab">
            <div className="custom-reports-header">
                <h3>Custom Reports</h3>
                <button
                    className="custom-reports-create-btn"
                    onClick={() => navigate('/reports/custom/new')}
                >
                    + Create New Report
                </button>
            </div>

            {configs.length === 0 ? (
                <div className="custom-reports-empty">
                    <div className="custom-reports-empty-icon">📊</div>
                    <div className="custom-reports-empty-title">No saved reports yet</div>
                    <div className="custom-reports-empty-text">
                        Create a custom report to analyze your fleet data.
                    </div>
                    <button
                        className="custom-reports-create-btn"
                        onClick={() => navigate('/reports/custom/new')}
                    >
                        + Create New Report
                    </button>
                </div>
            ) : (
                <div className="custom-reports-grid">
                    {configs.map((config) => (
                        <div
                            key={config.id}
                            className="custom-report-card"
                            onClick={() => handleCardClick(config.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleCardClick(config.id);
                                }
                            }}
                            aria-label={`Open ${config.reportName}`}
                        >
                            <div className="custom-report-card-top">
                                <span className="custom-report-card-name">{config.reportName}</span>
                                <div className="custom-report-kebab-wrapper" ref={openMenuId === config.id ? menuRef : undefined}>
                                    <button
                                        className="custom-report-kebab-btn"
                                        onClick={(e) => handleKebabClick(e, config.id)}
                                        aria-label={`More options for ${config.reportName}`}
                                        title="More options"
                                    >
                                        ⋮
                                    </button>
                                    {openMenuId === config.id && (
                                        <div className="custom-report-kebab-menu" role="menu">
                                            <button
                                                className="custom-report-kebab-option"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(config.id);
                                                }}
                                                role="menuitem"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="custom-report-card-metric">
                                {formatMetricLabel(config.metricKey)}
                            </div>
                            <div className="custom-report-card-date">
                                Last run: {formatDate(config.lastRunAt || config.createdAt)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
